import * as localForage from 'localforage';
import { applyMiddleware, compose, createStore } from 'redux';
import { persistReducer, persistStore, createMigrate } from 'redux-persist';
import createSagaMiddleware from 'redux-saga';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';

import { createRootReducer } from 'store/reducer';
import rootSaga from 'store/sagas';
import history from 'services/browser';
import { refreshTokenMiddleware } from 'store/RefreshTokenMiddleware';
import { isValidCustomerName } from 'services/route.service';


/**
 * Redux-persist migrations.
 *
 * v1: clean up legacy `homeState.customerName` values persisted to localForage
 * by older builds (which defaulted to the literal string "undefined"). The
 * REHYDRATE action bypasses reducer guards, so without this migration that
 * stale string would survive across sessions and be read as a valid customer
 * name, producing URLs like `/driverportal/undefined/login`. Any value that
 * fails `isValidCustomerName` is normalized to `null` so callers fall back to
 * parsing the realm from the URL.
 */
const migrations = {
  1: (state) => {
    if (!state) return state;
    return {
      ...state,
      homeState: {
        ...state.homeState,
        customerName: isValidCustomerName(state.homeState?.customerName)
          ? state.homeState.customerName
          : null,
      },
    };
  },
};

const persistConfig = {
  key: "driverportal",
  storage: localForage,
  stateReconciler: autoMergeLevel2,
  version: 1,
  migrate: createMigrate(migrations, { debug: false }),
  blacklist: ["euaState"],
};

export default function configureStore(initialState) {
  // Middlewares
  const sagaMiddleware = createSagaMiddleware();
  const middlewares = [refreshTokenMiddleware, sagaMiddleware];

  // Redux DevTools
  const composeEnhancers =
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

  // Root reducer with persistence
  const reducer = createRootReducer(history);
  const rootReducer = (state, action) => reducer(state, action);
  const persistedReducer = persistReducer(persistConfig, rootReducer);

  // Create store
  const store = createStore(
    persistedReducer,
    initialState,
    composeEnhancers(applyMiddleware(...middlewares))
  );

  // Persistor and sagas
  const persistor = persistStore(store);
  sagaMiddleware.run(rootSaga);

  return { store, persistor };
}
